import {
  BrandSettings,
  type ReportContent,
  type ReportStatus,
  type Signoff,
} from '@quickreport/domain';
import { buildReportData, type SignatoryInfo } from './build';
import type { ReportData } from './data';
import { qrSvg } from './qr';

export interface StoredImage {
  bytes: Uint8Array;
  mime: string;
}

/** A signatory as stored in the lab's data (the `signatories` table). */
export interface SignatoryRow {
  id: string;
  name: string;
  qualification: string;
  designation: string | null;
  reg_no: string | null;
  signature_file_id: string | null;
}

/** Where a device keeps what a report needs besides the report itself. */
export interface PrintSources {
  verifyBaseUrl: string;
  /** The lab's settings today. */
  currentBrand: BrandSettings;
  loadImage: (fileId: string | null | undefined) => Promise<StoredImage | null>;
  /** The signatories with these ids, for a report signed before sign-offs kept their own copy. */
  signatories: (ids: string[]) => Promise<SignatoryRow[]>;
}

export interface ReportForPrint {
  labNo: string;
  status: ReportStatus;
  verifyCode?: string | null;
  updatedAt: string;
  version?: number;
  content: ReportContent;
}

export interface AssembledReport {
  data: ReportData;
  assets: Record<string, Uint8Array>;
  /** Whether the printed page carries a verification QR (only once the server has issued a code). */
  hasQr: boolean;
}

export const extensionOf = (mime: string) =>
  mime === 'image/svg+xml'
    ? 'svg'
    : mime === 'image/jpeg'
      ? 'jpg'
      : mime === 'image/webp'
        ? 'webp'
        : 'png';

/** The lab's saved settings, or sensible starting values for a lab that has not saved any yet. */
export function readBrand(doc: unknown, defaults: Partial<BrandSettings> = {}): BrandSettings {
  const parsed = BrandSettings.safeParse(doc ?? defaults);
  const brand = parsed.success ? parsed.data : BrandSettings.parse({});
  // A lab that has not chosen yet gets the LabView line; saving the settings makes it explicit.
  return brand.labviewFooter === undefined ? { ...brand, labviewFooter: true } : brand;
}

/**
 * How a report looks when it is signed, kept with it: the lab's settings and who signed, so it
 * prints the same later even if the settings change. Web and Android sign through this.
 */
export function signoffFrom(brand: BrandSettings, signatories: SignatoryRow[]): Signoff {
  return {
    brand,
    signatories: signatories.map((x) => ({
      id: x.id,
      name: x.name,
      qualification: x.qualification,
      designation: x.designation,
      regNo: x.reg_no,
      signatureFileId: x.signature_file_id,
    })),
  };
}

/**
 * Everything the renderer needs to print a saved report exactly as it prints everywhere else:
 * the lab's settings, logo, letterhead, signatures, and a verification QR only when the server
 * has issued the report's code. The web app and the Android app both print through this.
 */
export async function assembleReport(
  report: ReportForPrint,
  src: PrintSources,
  brandOverride?: BrandSettings,
): Promise<AssembledReport> {
  const savedBrand = report.content.signoff?.brand;
  const current = src.currentBrand;
  // A report signed before the LabView footer setting existed follows the lab's choice today; one
  // signed after it keeps what it was signed with.
  const brand =
    brandOverride ??
    (savedBrand
      ? savedBrand.labviewFooter === undefined
        ? { ...savedBrand, labviewFooter: current.labviewFooter }
        : savedBrand
      : current);
  const assets: Record<string, Uint8Array> = {};

  const logo = await src.loadImage(brand.logoFileId);
  let logoPath: string | undefined;
  if (logo) {
    logoPath = `/assets/logo.${extensionOf(logo.mime)}`;
    assets[logoPath] = logo.bytes;
  }
  const background = await src.loadImage(brand.backgroundFileId);
  let backgroundPath: string | undefined;
  if (background) {
    backgroundPath = `/assets/background.${extensionOf(background.mime)}`;
    assets[backgroundPath] = background.bytes;
  }

  const signatories: SignatoryInfo[] = [];
  const snapshot = report.content.signoff?.signatories;
  if (snapshot?.length) {
    // Signed reports print with the signatories as they were at signing.
    for (const r of snapshot) {
      const img = await src.loadImage(r.signatureFileId);
      let signaturePath: string | undefined;
      if (img) {
        signaturePath = `/assets/sig-${r.id}.${extensionOf(img.mime)}`;
        assets[signaturePath] = img.bytes;
      }
      signatories.push({
        id: r.id,
        name: r.name,
        qualification: r.qualification,
        designation: r.designation ?? null,
        regNo: r.regNo ?? null,
        ...(signaturePath ? { signaturePath } : {}),
      });
    }
  } else {
    const signedIds = [
      ...new Set(report.content.sections.map((s) => s.signedBy).filter(Boolean)),
    ] as string[];
    if (signedIds.length) {
      for (const r of await src.signatories(signedIds)) {
        const img = await src.loadImage(r.signature_file_id);
        let signaturePath: string | undefined;
        if (img) {
          signaturePath = `/assets/sig-${r.id}.${extensionOf(img.mime)}`;
          assets[signaturePath] = img.bytes;
        }
        signatories.push({
          id: r.id,
          name: r.name,
          qualification: r.qualification,
          designation: r.designation,
          regNo: r.reg_no,
          ...(signaturePath ? { signaturePath } : {}),
        });
      }
    }
  }

  let verify: { code: string; qrPath: string } | undefined;
  if (report.verifyCode) {
    assets['/assets/qr.svg'] = await qrSvg(`${src.verifyBaseUrl}/r/${report.verifyCode}`);
    verify = { code: report.verifyCode, qrPath: '/assets/qr.svg' };
  }

  const signedAt = report.content.sections.map((s) => s.signedAt).find(Boolean) ?? report.updatedAt;
  const data = buildReportData({
    labNo: report.labNo,
    content: report.content,
    brand,
    signatories,
    ...(logoPath ? { logoPath } : {}),
    ...(backgroundPath ? { backgroundPath } : {}),
    ...(verify ? { verify } : {}),
    signedAt: new Date(signedAt),
    status: report.status,
    ...(report.version ? { version: report.version } : {}),
  });
  return { data, assets, hasQr: Boolean(verify) };
}

/** The file name a report's PDF is saved and shared under. */
export const reportFileName = (labNo: string, patientName: string) =>
  `${labNo}-${patientName.replace(/[^\w]+/g, '-')}.pdf`;
